import { gql } from '@apollo/client';
import { isRetailCodeScan, type ProductScanLike } from '@/lib/scanning/retailCode';
import type { WarehouseProductShape } from '@/lib/utils/deaScheduleUtils';

/**
 * Home-screen lookup for a product's retail barcode: every product of the company's the code
 * names, each with the zones holding it (Pick Path order) and every zone's lots, soonest-expiring
 * first. Empty when the code names nothing the company carries or holds.
 */
export const WAREHOUSE_PRODUCT_STOCK_BY_CODE = gql`
  query warehouseProductStockByCode($companyId: ID!, $code: String!) {
    warehouseProductStockByCode(companyId: $companyId, code: $code) {
      fdaProductId
      upcProductId
      productId
      productName
      ndc
      upc
      deaSchedule
      stocked
      quarantined
      zones {
        zone {
          id
          name
          zoneType
        }
        locationLabel
        stocked
        quarantined
        lots {
          zoneInventoryBalanceId
          lot
          expiration
          stocked
          quarantined
        }
      }
    }
  }
`;

export type ProductStockLot = {
  zoneInventoryBalanceId: string;
  lot: string | null;
  expiration: string | number | null;
  stocked: number;
  quarantined: number;
};

export type ProductStockZone = {
  zone: { id: string; name: string | null; zoneType: string | null };
  locationLabel: string | null;
  stocked: number;
  quarantined: number;
  lots: ProductStockLot[];
};

export type ProductStock = {
  fdaProductId: string | null;
  upcProductId: string | null;
  productId: string | null;
  productName: string;
  ndc: string | null;
  upc: string | null;
  deaSchedule: string | null;
  stocked: number;
  quarantined: number;
  zones: ProductStockZone[];
};

/**
 * The retail barcode (UPC-A / EAN-13 / UPC-E / EAN-8 / GTIN-14) a home-screen scan carries, as read,
 * or null when the scan is anything else. Recognised from the digits, never from `kind` — see
 * retailCode.ts: some valid UPCs parse as a GS1 element string, with a bogus serial or lot.
 */
export const productCodeFromScan = (result: ProductScanLike): string | null =>
  isRetailCodeScan(result) ? (result?.text || '').trim() : null;

export const scannedProductPath = (code: string) =>
  `/scanned-product?code=${encodeURIComponent(code)}`;

/**
 * The code as printed under the bars: a UPC-A read as its EAN-13 / GTIN-14 form (what Scandit
 * returns) loses the leading zeros it gained. Anything else is shown as scanned.
 */
export const displayUpc = (code: string): string =>
  /^0{1,2}\d{12}$/.test(code) ? code.slice(-12) : code;

/**
 * The shape WarehouseProductDisplay reads: title, schedule pill, NDC or UPC. A UPC product shows the
 * scanned code as printed — the stored UPC is raw and may have lost its leading zero.
 */
export const toWarehouseProduct = (stock: ProductStock, code: string): WarehouseProductShape => ({
  title: stock.productName,
  fdaProduct: stock.fdaProductId ? { elevenDigitNdc: stock.ndc, deaSchedule: stock.deaSchedule } : null,
  upcProduct: stock.upcProductId ? { upc: displayUpc(code) } : null
});
